import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Icon, Popover } from 'antd';
import styled from 'styled-components';
import { SETTINGS_SECTIONS } from './settingsItems';

import { request } from '~/components/Instance';
import ApifyIntegrationDrawer from './ApifyIntegrationDrawer';

var SUPABASE_URL = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SUPABASE_URL) || '';
var SUPABASE_ANON_KEY = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SUPABASE_ANON_KEY) || '';

var GridContainer = styled.div`
  padding: 0;
`;

var SectionGroup = styled.div`
  margin-bottom: 28px;

  &:last-child {
    margin-bottom: 0;
  }
`;

var SectionGroupLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 12px;
`;

var CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
`;

var Card = styled.div`
  background: ${function(p) { return p.disabled ? '#fafafa' : '#fff'; }};
  border: 1px solid #f0f0f0;
  border-left: 3px solid transparent;
  border-radius: 8px;
  padding: 24px 26px;
  cursor: ${function(p) { return p.disabled ? 'not-allowed' : 'pointer'; }};
  transition: all 0.2s ease;
  display: flex;
  align-items: flex-start;
  gap: 18px;
  min-height: 110px;
  position: relative;
  opacity: ${function(p) { return p.disabled ? 0.6 : 1; }};

  &:hover {
    border-color: ${function(p) { return p.disabled ? '#f0f0f0' : '#d9d9d9'; }};
    border-left-color: ${function(p) { return p.disabled ? 'transparent' : (p.hoverColor || '#1890ff'); }};
    box-shadow: ${function(p) { return p.disabled ? 'none' : '0 4px 16px rgba(0, 0, 0, 0.08)'; }};
    transform: ${function(p) { return p.disabled ? 'none' : 'translateY(-2px)'; }};
  }

  &:active {
    transform: ${function(p) { return p.disabled ? 'none' : 'translateY(0)'; }};
    box-shadow: ${function(p) { return p.disabled ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.06)'; }};
  }
`;

var CardIconBox = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${function(p) { return p.bg || '#f0f5ff'; }};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.2s;

  ${Card}:hover & {
    transform: scale(1.05);
  }

  .anticon {
    font-size: 20px;
    color: ${function(p) { return p.color || '#1890ff'; }};
  }
`;

var CardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

var CardTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 3px;
`;

var CardDescription = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  line-height: 1.4;
`;

var CardChildrenLink = styled.div`
  font-size: 11px;
  color: #bfbfbf;
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;

  &:hover {
    color: #1890ff;
  }

  .anticon {
    font-size: 10px;
  }
`;

var ChildrenPopoverList = styled.div`
  min-width: 160px;
`;

var ChildrenPopoverItem = styled.div`
  padding: 6px 8px;
  font-size: 13px;
  color: #595959;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: #f0f5ff;
    color: #1890ff;
  }

  .anticon {
    font-size: 12px;
    color: #bfbfbf;
  }
`;

var WarningBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: #fff1f0;
  color: #ff4d4f;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  padding: 3px 6px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  white-space: nowrap;
  max-width: calc(100% - 16px);
  animation: pulse 2s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  .anticon {
    font-size: 10px;
    line-height: 1;
  }
`;

var ConnectedBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%);
  color: #389e0d;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  padding: 4px 8px;
  border-radius: 10px;
  border: 1px solid #b7eb8f;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
  max-width: calc(100% - 16px);

  .pulse-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #52c41a;
    box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.5);
    animation: connected-pulse 2s ease-in-out infinite;
  }

  @keyframes connected-pulse {
    0%   { box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.5); }
    70%  { box-shadow: 0 0 0 6px rgba(82, 196, 26, 0); }
    100% { box-shadow: 0 0 0 0 rgba(82, 196, 26, 0); }
  }
`;

var ComingSoonBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: #f0f0f0;
  color: #8c8c8c;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  padding: 3px 6px;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

var CardArrow = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0;
  transition: opacity 0.2s, transform 0.2s;
  color: #bfbfbf;

  ${Card}:hover & {
    opacity: 1;
    transform: translateY(-50%) translateX(2px);
  }
`;

// AntD v3 doesn't ship a `whatsapp` brand icon, so render the official glyph
// inline. Color is driven by `currentColor` so CardIconBox's color rule applies.
var WhatsAppGlyph = function() {
  return React.createElement('svg', {
    viewBox: '0 0 24 24',
    width: '1em',
    height: '1em',
    fill: 'currentColor',
    'aria-hidden': true,
  },
    React.createElement('path', {
      d: 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z',
    })
  );
};

var renderItemIcon = function(item) {
  if (item.icon === 'whatsapp') {
    return React.createElement(Icon, { component: WhatsAppGlyph });
  }
  return React.createElement(Icon, { type: item.icon });
};

var REQUIRED_COMPANY_FIELDS = ['name', 'legal_name', 'national_register', 'accountant', 'crc'];

var getCompanyWarning = function(company) {
  if (!company) return null;
  var missing = REQUIRED_COMPANY_FIELDS.filter(function(field) {
    return !company[field];
  });
  if (missing.length === 0) return null;
  return missing.length === 1 ? '1 pendente' : missing.length + ' pendentes';
};

var SettingsCardGrid = function(props) {
  var hasPermission = props.hasPermission;
  var isAdmin = props.isAdmin;
  var company = props.company;
  var history = useHistory();
  var companyWarning = getCompanyWarning(company);

  // Surface integration state directly on each card so the user knows what
  // is configured without having to open the page/drawer.
  var [instagramConnection, setInstagramConnection] = useState(null);
  var [apifyConfigured, setApifyConfigured] = useState(false);
  var igCardVisible = true;

  useEffect(function() {
    if (!igCardVisible || !SUPABASE_URL) return;
    var cancelled = false;

    request('GET', '/supabase-token', null, false)
      .then(function(res) {
        var token = res && res.data && res.data.token;
        if (!token) return null;

        // Fetch Instagram + Apify status in parallel.
        var ig = fetch(
          SUPABASE_URL + '/rest/v1/instagram_connections?is_active=eq.true&select=username,last_event_at',
          {
            headers: {
              'Authorization': 'Bearer ' + token,
              'apikey': SUPABASE_ANON_KEY,
            },
          }
        ).then(function(r) { return r.ok ? r.json() : []; });

        var apify = fetch(
          SUPABASE_URL + '/functions/v1/apify-integrations',
          {
            headers: {
              'Authorization': 'Bearer ' + token,
              'apikey': SUPABASE_ANON_KEY,
            },
          }
        ).then(function(r) { return r.ok ? r.json() : null; });

        return Promise.all([ig, apify]);
      })
      .then(function(both) {
        if (cancelled || !both) return;
        var igRows = both[0];
        var apifyData = both[1];
        setInstagramConnection(Array.isArray(igRows) && igRows.length ? igRows[0] : null);
        // The card shows "configurado" when at least one active integration
        // exists with a token in place.
        var hasActive = false;
        if (apifyData && Array.isArray(apifyData.integrations)) {
          for (var i = 0; i < apifyData.integrations.length; i++) {
            if (apifyData.integrations[i].is_active && apifyData.integrations[i].has_token) {
              hasActive = true;
              break;
            }
          }
        }
        setApifyConfigured(hasActive);
      })
      .catch(function() {
        if (!cancelled) {
          setInstagramConnection(null);
          setApifyConfigured(false);
        }
      });
    return function() { cancelled = true; };
  }, [igCardVisible]);

  // Items can either navigate to a route (`item.path`) or open a setup
  // drawer in-place (`item.drawer`). Drawers stay rendered inside this
  // component so the card grid owns its state.
  var [openDrawer, setOpenDrawer] = useState(null);

  var handleCardClick = function(item) {
    if (item.drawer) {
      setOpenDrawer(item.drawer);
      return;
    }
    if (item.path) history.push(item.path);
  };

  var closeDrawer = function() { setOpenDrawer(null); };

  var handleChildClick = function(child, e) {
    if (e) e.stopPropagation();
    history.push(child.path);
  };

  var filteredSections = SETTINGS_SECTIONS.map(function(section) {
    if (section.adminOnly && !isAdmin) return null;

    var visibleItems = section.items.filter(function(item) {
      if (item.adminOnly && !isAdmin) return false;
      if (item.permission && !hasPermission(item.permission)) return false;
      return true;
    });

    if (visibleItems.length === 0) return null;

    return { section: section, items: visibleItems };
  }).filter(Boolean);

  return React.createElement(GridContainer, null,
    React.createElement(ApifyIntegrationDrawer, {
      visible: openDrawer === 'apify-instagram',
      onClose: closeDrawer,
    }),
    filteredSections.map(function(group) {
      var sectionColor = group.section.color || '#1890ff';
      var sectionBg = group.section.bg || '#f0f5ff';

      return React.createElement(SectionGroup, { key: group.section.key },
        React.createElement(SectionGroupLabel, null, group.section.label),
        React.createElement(CardsGrid, null,
          group.items.map(function(item) {
            var childrenEl = null;

            return React.createElement(Card, {
              key: item.key,
              hoverColor: sectionColor,
              disabled: item.disabled,
              onClick: item.disabled ? undefined : function() { handleCardClick(item); }
            },
              item.comingSoon && React.createElement(ComingSoonBadge, null, 'Em breve'),
              item.key === 'empresa' && companyWarning && React.createElement(WarningBadge, null,
                React.createElement(Icon, { type: 'warning' }),
                companyWarning
              ),
              item.key === 'instagram' && instagramConnection && instagramConnection.username && React.createElement(
                ConnectedBadge,
                { title: 'Conectado como @' + instagramConnection.username },
                React.createElement('span', { className: 'pulse-dot' }),
                '@' + instagramConnection.username
              ),
              item.key === 'apify-instagram' && apifyConfigured && React.createElement(
                ConnectedBadge,
                { title: 'Integração configurada' },
                React.createElement('span', { className: 'pulse-dot' }),
                'configurado'
              ),
              React.createElement(CardIconBox, {
                color: item.iconColor || sectionColor,
                bg: item.iconBg || sectionBg,
              },
                renderItemIcon(item)
              ),
              React.createElement(CardContent, null,
                React.createElement(CardTitle, null, item.label),
                item.description && React.createElement(CardDescription, null, item.description),
                childrenEl
              ),
              !item.disabled && React.createElement(CardArrow, null,
                React.createElement(Icon, { type: 'right' })
              )
            );
          })
        )
      );
    })
  );
};

export default SettingsCardGrid;
